/**
 * Typed client for the calendar service (ADR 0008), reached same-origin through the `/calendar`
 * prefix (the Vite dev proxy forwards it to the contract port 9083; the service strips the
 * prefix itself). Every product route requires a Keycloak bearer token - the SPA's SSO session
 * access token. Error responses carry the contract envelope {"error":{code,message}}.
 *
 * Response bodies are narrowed explicitly (unknown -> typed) instead of cast, mirroring
 * src/api/join.ts; a shape violation raises CalendarApiError('invalid_response') naming the
 * field, so a drifting contract fails loudly instead of leaking `any` into the UI.
 */

import { browserSsoStorage, refreshSsoSession, ssoConfigFromEnv } from '../auth/sso';
import { createRoom } from '../api/rooms';

/** Wire shape of one calendar event (docs/contract.md change request for M8). */
export interface CalendarEvent {
  readonly id: number;
  readonly uid: string;
  readonly title: string;
  readonly description: string | null;
  readonly location: string | null;
  /** RFC 3339 timestamp; for occurrences this stays the series' base start. */
  readonly startsAt: string;
  /** RFC 3339 timestamp; strictly after startsAt; base end for occurrences. */
  readonly endsAt: string;
  readonly allDay: boolean;
  readonly organizer: string | null;
  /** Control-service room slug (^[a-z0-9][a-z0-9-]{2,63}$) when the event links a live room. */
  readonly roomSlug: string | null;
  /** 'local' for events created in the SPA; anything else is an imported, read-only mirror. */
  readonly source: string;
  readonly rrule: string | null;
  /** Participant roster ("Name" / "Name <email>" strings); empty when none recorded. */
  readonly participants: readonly string[];
  /** This listing entry's occurrence window; null for non-recurring events and single fetches. */
  readonly occurrenceStartsAt: string | null;
  readonly occurrenceEndsAt: string | null;
  /** RRULE expansion outcome of the listing: true expanded, false fallback, null non-recurring. */
  readonly expanded: boolean | null;
  /** Why expansion fell back to the base occurrence; null when expanded or non-recurring. */
  readonly expansionNote: string | null;
  /** Display color as an #rrggbb literal; null when the event has none. */
  readonly color: string | null;
  /** Scheduling intents (CR-21): authoring metadata for the linked room; false by default. */
  readonly recordingIntent: boolean;
  readonly lobbyIntent: boolean;
  /** The registration cap (CR-27); null = unlimited. */
  readonly capacity: number | null;
}

/**
 * Omit that distributes over a union: a plain `Omit<SourceConfig, 'kind'>` collapses the
 * 4-member union to `{}` (keyof of a union is the shared keys), so the config fields would be
 * untyped. Distributed member-wise, every kind keeps its own field set.
 */
export type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

/** Config payload of one source kind, exactly the fields its sync engine accepts. */
export type SourceConfig =
  | { readonly kind: 'caldav'; readonly serverUrl: string; readonly collectionPath: string }
  | {
      readonly kind: 'ews';
      readonly url: string;
      readonly mailbox: string;
      readonly allowHttp: boolean;
      readonly allowNtlm: boolean;
    }
  | {
      readonly kind: 'ad';
      readonly host: string;
      readonly port: number;
      readonly startTls: boolean;
      readonly baseDn: string;
      readonly filter: string;
    }
  /** CR-31: a published iCalendar feed (e.g. a Google secret iCal link); read-only. */
  | { readonly kind: 'ics'; readonly url: string };

/** Wire shape of one configured external calendar source; credentials never come back. */
export interface CalendarSource {
  readonly id: number;
  readonly kind: 'caldav' | 'ews' | 'ad' | 'ics';
  readonly name: string;
  readonly config: SourceConfig;
  readonly enabled: boolean;
  readonly syncIntervalSecs: number;
  readonly lastSyncAt: string | null;
  readonly lastSyncError: string | null;
}

/** One imported AD room of the directory (resources, not events). */
export interface AdRoom {
  readonly dn: string;
  readonly name: string;
  readonly mail: string | null;
  readonly importedAt: string | null;
}

/** Write-only credentials of a create/patch source body; the server seals them before storage. */
export interface SourceCredentials {
  readonly username: string;
  readonly password: string;
}

/** Body of POST /api/v1/events. */
export interface CreateEventBody {
  readonly title: string;
  readonly startsAt: string;
  readonly endsAt: string;
  readonly description?: string;
  readonly location?: string;
  readonly allDay?: boolean;
  readonly roomSlug?: string;
  readonly participants?: readonly string[];
  /** #rrggbb literal; absent or blank stores as null (no color). */
  readonly color?: string;
  /** Scheduling intents (CR-21): absent = false. Authoring metadata for the linked room. */
  readonly recordingIntent?: boolean;
  readonly lobbyIntent?: boolean;
  /** The registration cap (CR-27): absent = unlimited; 0 = unlimited too. */
  readonly capacity?: number;
}

/**
 * Body of PATCH /api/v1/events/{id}: present fields replace stored values, absent fields keep
 * them. Clearing is shape-specific (the wire uses pointers, so JSON null means "absent/keep"):
 * description, location, roomSlug, rrule and color clear via "" (the server stores null),
 * participants clear via []. color carries an #rrggbb literal when set.
 */
export interface PatchEventBody {
  readonly title?: string;
  readonly description?: string;
  readonly location?: string;
  readonly startsAt?: string;
  readonly endsAt?: string;
  readonly allDay?: boolean;
  readonly roomSlug?: string;
  readonly rrule?: string;
  readonly participants?: readonly string[];
  readonly color?: string;
  /** Scheduling intents (CR-21): absent = keep, present replaces. */
  readonly recordingIntent?: boolean;
  readonly lobbyIntent?: boolean;
  /** The registration cap (CR-27): absent = keep, 0 = clear (unlimited), 1..100000 = replace. */
  readonly capacity?: number;
}

/** One VEVENT the import did not place, with the reason from the server's closed vocabulary. */
export interface ImportSkipped {
  readonly uid: string;
  readonly reason: string;
}

/** One non-fatal import note ("rrule_downgraded:<reason>" / "invalid_color_ignored"). */
export interface ImportWarning {
  readonly uid: string;
  readonly note: string;
}

/** Response of POST /api/v1/events/import.ics. */
export interface ImportResult {
  readonly imported: number;
  readonly updated: number;
  readonly failed: number;
  readonly skipped: readonly ImportSkipped[];
  readonly warnings: readonly ImportWarning[];
}

/** Body of POST /api/v1/sources. */
export interface CreateSourceBody {
  readonly kind: 'caldav' | 'ews' | 'ad' | 'ics';
  readonly name: string;
  readonly config: DistributiveOmit<SourceConfig, 'kind'>;
  readonly credentials?: SourceCredentials;
  readonly syncIntervalSecs: number;
}

/** Body of PATCH /api/v1/sources/{id}: present fields replace stored values. */
export interface PatchSourceBody {
  readonly name?: string;
  readonly config?: DistributiveOmit<SourceConfig, 'kind'>;
  readonly credentials?: SourceCredentials;
  readonly enabled?: boolean;
  readonly syncIntervalSecs?: number;
}

/** Closed failure vocabulary of the connection-check endpoints (CR-33). */
export type SourceCheckCode = 'unreachable' | 'unauthorized' | 'server_error' | 'not_calendar';

/**
 * One calendar collection a successful check discovered (caldav only): the path is the click
 * target the settings form offers for collectionPath. The backend sends displayName as a plain
 * string - "" when the collection has no display name, never null (check.Result.Calendar).
 */
export interface SourceCheckCalendar {
  readonly path: string;
  readonly displayName: string;
}

/**
 * Answer of the connection-check endpoints (CR-33): HTTP 200 either way - a bad connection is
 * the answer, not an error. ok=true carries the discovered calendars (caldav only); ok=false
 * carries a {@link SourceCheckCode} plus the English detail message for secondary display.
 */
export interface SourceCheck {
  readonly ok: boolean;
  readonly calendars: readonly SourceCheckCalendar[];
  readonly code: SourceCheckCode | null;
  readonly message: string | null;
}

/** Failure of a calendar API call: HTTP status, contract error code, message. */
export class CalendarApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'CalendarApiError';
    this.status = status;
    this.code = code;
  }

  /** True when the server rejected the bearer token (or it was missing). */
  get unauthorized(): boolean {
    return this.status === 401;
  }
}

/** Same-origin base of the calendar product API. */
export const CALENDAR_API_BASE = '/calendar/api/v1';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function nullableString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function finiteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function parseIsoDate(value: unknown, field: string): string {
  const raw = nonEmptyString(value);
  if (raw === null || Number.isNaN(Date.parse(raw))) {
    throw new TypeError(`calendar response field "${field}" must be an RFC 3339 timestamp`);
  }
  return raw;
}

function parseNullableIsoDate(value: unknown, field: string): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  return parseIsoDate(value, field);
}

/** Narrows one event's optional occurrence/expansion fields; absent stays null. */
function parseOccurrenceFields(value: Record<string, unknown>): {
  readonly occurrenceStartsAt: string | null;
  readonly occurrenceEndsAt: string | null;
  readonly expanded: boolean | null;
  readonly expansionNote: string | null;
} {
  return {
    occurrenceStartsAt: parseNullableIsoDate(value.occurrenceStartsAt, 'occurrenceStartsAt'),
    occurrenceEndsAt: parseNullableIsoDate(value.occurrenceEndsAt, 'occurrenceEndsAt'),
    expanded: typeof value.expanded === 'boolean' ? value.expanded : null,
    expansionNote: nullableString(value.expansionNote),
  };
}

/** Narrows one event object; throws TypeError naming the first violating field. */
export function parseCalendarEvent(value: unknown): CalendarEvent {
  if (!isRecord(value)) {
    throw new TypeError('calendar event must be a JSON object');
  }
  const id = finiteNumber(value.id);
  if (id === null || !Number.isInteger(id) || id <= 0) {
    throw new TypeError('calendar event field "id" must be a positive integer');
  }
  const uid = nonEmptyString(value.uid);
  if (uid === null) {
    throw new TypeError('calendar event field "uid" must be a non-empty string');
  }
  if (typeof value.title !== 'string') {
    throw new TypeError('calendar event field "title" must be a string');
  }
  if (typeof value.allDay !== 'boolean') {
    throw new TypeError('calendar event field "allDay" must be a boolean');
  }
  const source = nonEmptyString(value.source);
  if (source === null) {
    throw new TypeError('calendar event field "source" must be a non-empty string');
  }
  let participants: readonly string[] = [];
  if (value.participants !== undefined && value.participants !== null) {
    participants = parseArray(value.participants, 'participants').map((entry) => {
      if (typeof entry !== 'string') {
        throw new TypeError('calendar event field "participants" must hold strings');
      }
      return entry;
    });
  }
  return {
    id,
    uid,
    title: value.title,
    description: nullableString(value.description),
    location: nullableString(value.location),
    startsAt: parseIsoDate(value.startsAt, 'startsAt'),
    endsAt: parseIsoDate(value.endsAt, 'endsAt'),
    allDay: value.allDay,
    organizer: nullableString(value.organizer),
    roomSlug: nonEmptyString(value.roomSlug),
    source,
    rrule: nullableString(value.rrule),
    participants,
    color: nullableString(value.color),
    // CR-21: always present on the wire; a pre-CR-21 server's absence clamps to false.
    recordingIntent: value.recordingIntent === true,
    lobbyIntent: value.lobbyIntent === true,
    // CR-27: optional cap; null/absent = unlimited; a non-integer value fails the parse.
    capacity: nullableInteger(value.capacity, 'capacity'),
    ...parseOccurrenceFields(value),
  };
}

/** Nullable integer: null/absent passes as null, anything but an integer fails. */
function nullableInteger(value: unknown, field: string): number | null {
  if (value === undefined || value === null) {
    return null;
  }
  if (typeof value !== 'number' || !Number.isInteger(value)) {
    throw new TypeError(`calendar event field "${field}" must be an integer or null`);
  }
  return value;
}

const SOURCE_KINDS = ['caldav', 'ews', 'ad', 'ics'] as const;

/** Narrows one source config payload to its kind's shape; unknown variants are rejected. */
export function parseSourceConfig(kind: unknown, raw: unknown): SourceConfig {
  const sourceKind = SOURCE_KINDS.find((candidate) => candidate === kind);
  if (sourceKind === undefined) {
    throw new TypeError(`calendar source field "kind" must be one of caldav, ews, ad, ics`);
  }
  if (!isRecord(raw)) {
    throw new TypeError('calendar source field "config" must be a JSON object');
  }
  if (sourceKind === 'caldav') {
    const serverUrl = nonEmptyString(raw.serverUrl);
    const collectionPath = nonEmptyString(raw.collectionPath);
    if (serverUrl === null || collectionPath === null) {
      throw new TypeError('caldav config needs serverUrl and collectionPath strings');
    }
    return { kind: 'caldav', serverUrl, collectionPath };
  }
  if (sourceKind === 'ews') {
    const url = nonEmptyString(raw.url);
    const mailbox = nonEmptyString(raw.mailbox);
    if (url === null || mailbox === null) {
      throw new TypeError('ews config needs url and mailbox strings');
    }
    return {
      kind: 'ews',
      url,
      mailbox,
      allowHttp: raw.allowHttp === true,
      allowNtlm: raw.allowNtlm === true,
    };
  }
  if (sourceKind === 'ics') {
    const url = nonEmptyString(raw.url);
    if (url === null) {
      throw new TypeError('ics config needs a url string');
    }
    return { kind: 'ics', url };
  }
  const host = nonEmptyString(raw.host);
  const baseDn = nonEmptyString(raw.baseDn);
  const port = finiteNumber(raw.port);
  if (host === null || baseDn === null || port === null) {
    throw new TypeError('ad config needs host, baseDn and a numeric port');
  }
  return {
    kind: 'ad',
    host,
    port,
    startTls: raw.startTls === true,
    baseDn,
    filter: typeof raw.filter === 'string' ? raw.filter : '',
  };
}

/** Narrows one source object; throws TypeError naming the first violating field. */
export function parseCalendarSource(value: unknown): CalendarSource {
  if (!isRecord(value)) {
    throw new TypeError('calendar source must be a JSON object');
  }
  const id = finiteNumber(value.id);
  if (id === null || !Number.isInteger(id) || id <= 0) {
    throw new TypeError('calendar source field "id" must be a positive integer');
  }
  const config = parseSourceConfig(value.kind, value.config ?? {});
  const interval = finiteNumber(value.syncIntervalSecs);
  return {
    id,
    kind: config.kind,
    name: typeof value.name === 'string' ? value.name : '',
    config,
    enabled: value.enabled === true,
    syncIntervalSecs: interval ?? 0,
    lastSyncAt: parseNullableIsoDate(value.lastSyncAt, 'lastSyncAt'),
    lastSyncError: nullableString(value.lastSyncError),
  };
}

/** Narrows one AD room object. */
export function parseAdRoom(value: unknown): AdRoom {
  if (!isRecord(value)) {
    throw new TypeError('ad room must be a JSON object');
  }
  const dn = nonEmptyString(value.dn);
  const name = nonEmptyString(value.name);
  if (dn === null || name === null) {
    throw new TypeError('ad room needs "dn" and "name" strings');
  }
  return {
    dn,
    name,
    mail: nonEmptyString(value.mail),
    importedAt: parseNullableIsoDate(value.importedAt, 'importedAt'),
  };
}

function parseArray(value: unknown, what: string): unknown[] {
  if (!Array.isArray(value)) {
    throw new TypeError(`calendar response field "${what}" must be an array`);
  }
  return value;
}

const SOURCE_CHECK_CODES = ['unreachable', 'unauthorized', 'server_error', 'not_calendar'] as const;

/** Narrows one discovered calendar collection row of a check answer. */
function parseSourceCheckCalendar(value: unknown): SourceCheckCalendar {
  if (!isRecord(value) || typeof value.path !== 'string') {
    throw new TypeError('check response calendars must carry a "path" string');
  }
  return {
    path: value.path,
    displayName: typeof value.displayName === 'string' ? value.displayName : '',
  };
}

/**
 * Narrows one connection-check answer (CR-33): ok=true carries the optional calendars array;
 * ok=false carries a code from the closed vocabulary plus the English detail message (the
 * backend always sends one). Throws TypeError naming the first violating field.
 */
export function parseSourceCheck(value: unknown): SourceCheck {
  if (!isRecord(value)) {
    throw new TypeError('check response must be a JSON object');
  }
  if (typeof value.ok !== 'boolean') {
    throw new TypeError('check response field "ok" must be a boolean');
  }
  if (value.ok) {
    let calendars: readonly SourceCheckCalendar[] = [];
    if (value.calendars !== undefined && value.calendars !== null) {
      calendars = parseArray(value.calendars, 'calendars').map(parseSourceCheckCalendar);
    }
    return { ok: true, calendars, code: null, message: nullableString(value.message) };
  }
  const code = SOURCE_CHECK_CODES.find((candidate) => candidate === value.code);
  if (code === undefined) {
    throw new TypeError(
      'check response field "code" must be one of unreachable, unauthorized, server_error, not_calendar',
    );
  }
  if (typeof value.message !== 'string') {
    throw new TypeError('check response field "message" must be a string');
  }
  return { ok: false, calendars: [], code, message: value.message };
}

/** Builds a CalendarApiError from a non-2xx response, reading the contract envelope when present. */
async function errorFromResponse(response: Response, endpoint: string): Promise<CalendarApiError> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // Non-JSON error body: keep the generic message.
  }
  let code = 'unknown_error';
  let message = `${endpoint} request failed with HTTP ${response.status}`;
  if (isRecord(body) && isRecord(body.error)) {
    if (typeof body.error.code === 'string') {
      code = body.error.code;
    }
    if (typeof body.error.message === 'string') {
      message = body.error.message;
    }
  }
  return new CalendarApiError(response.status, code, message);
}

/**
 * One silent session renewal from the stored refresh token. Returns the new access token, or
 * null when renewal is impossible (no stored session, grant rejected, storage unavailable).
 * The renewal goes through the shared SSO store (all tabs, single-flighted, lock-serialized).
 */
async function recoverAccessToken(fetchImpl: typeof fetch): Promise<string | null> {
  const storage = browserSsoStorage();
  if (storage === null) {
    return null;
  }
  try {
    const outcome = await refreshSsoSession(ssoConfigFromEnv(), storage, fetchImpl);
    return outcome.kind === 'refreshed' ? outcome.accessToken : null;
  } catch {
    return null;
  }
}

/**
 * Fetch helper shared by every call: injectable fetch (tests), JSON error envelope mapping and
 * a bearer token that the caller took from the SSO session. A 401 here triggers ONE silent
 * refresh-token rotation and a single retry - an access token that expired while the tab was
 * idle heals in place instead of kicking the user to the sign-in gate.
 */
async function request(
  path: string,
  init: { method: string; token: string; body?: unknown },
  fetchImpl: typeof fetch,
  endpoint: string,
): Promise<Response> {
  const attempt = async (token: string): Promise<Response> => {
    try {
      return await fetchImpl(`${CALENDAR_API_BASE}${path}`, {
        method: init.method,
        headers: {
          Authorization: `Bearer ${token}`,
          ...(init.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        },
        ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
      });
    } catch {
      throw new CalendarApiError(0, 'network_error', 'calendar service is unreachable');
    }
  };
  let response = await attempt(init.token);
  if (response.status === 401) {
    const renewed = await recoverAccessToken(fetchImpl);
    if (renewed !== null) {
      response = await attempt(renewed);
    }
  }
  if (!response.ok) {
    throw await errorFromResponse(response, endpoint);
  }
  return response;
}

async function requestJson(
  path: string,
  init: { method: string; token: string; body?: unknown },
  fetchImpl: typeof fetch,
  endpoint: string,
): Promise<unknown> {
  const response = await request(path, init, fetchImpl, endpoint);
  try {
    return (await response.json()) as unknown;
  } catch {
    throw new CalendarApiError(
      response.status,
      'invalid_response',
      `${endpoint} is not valid JSON`,
    );
  }
}

/** Wraps a response-parsing failure into the invalid_response API error. */
function parseError(status: number, endpoint: string, cause: unknown): CalendarApiError {
  const detail = cause instanceof TypeError ? cause.message : `malformed ${endpoint} response`;
  return new CalendarApiError(status, 'invalid_response', detail);
}

/** Lists events with startsAt in [from, to] (RFC 3339 bounds). */
export async function listEvents(
  token: string,
  range: { readonly from: string; readonly to: string },
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<CalendarEvent[]> {
  const query = new URLSearchParams({ from: range.from, to: range.to });
  const body = await requestJson(
    `/events?${query.toString()}`,
    { method: 'GET', token },
    fetchImpl,
    'list events',
  );
  if (!isRecord(body)) {
    throw new CalendarApiError(
      200,
      'invalid_response',
      'list events response is not a JSON object',
    );
  }
  try {
    return parseArray(body.events, 'events').map(parseCalendarEvent);
  } catch (cause: unknown) {
    throw parseError(200, 'list events', cause);
  }
}

/** Creates a local event; the server pins source='local' and answers 201. */
export async function createEvent(
  token: string,
  body: CreateEventBody,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<CalendarEvent> {
  const payload = await requestJson(
    '/events',
    { method: 'POST', token, body },
    fetchImpl,
    'create event',
  );
  try {
    return parseCalendarEvent(payload);
  } catch (cause: unknown) {
    const detail = cause instanceof TypeError ? cause.message : 'malformed create event response';
    throw new CalendarApiError(201, 'invalid_response', detail);
  }
}

/**
 * Fetches one event by id (GET /api/v1/events/{id}, base times - no occurrence fields): the
 * webinar detail page reads the full row this way (description, capacity, organizer). 404
 * surfaces as CalendarApiError.
 */
export async function getEvent(
  token: string,
  id: number,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<CalendarEvent> {
  const payload = await requestJson(
    `/events/${String(id)}`,
    { method: 'GET', token },
    fetchImpl,
    'get event',
  );
  try {
    return parseCalendarEvent(payload);
  } catch (cause: unknown) {
    const detail = cause instanceof TypeError ? cause.message : 'malformed get event response';
    throw new CalendarApiError(200, 'invalid_response', detail);
  }
}

/**
 * Patches one event (present fields replace, absent keep); imported rows answer 409
 * read_only_source. The body is passed through verbatim - the "" / [] clear semantics live in
 * {@link PatchEventBody} and are the caller's responsibility.
 */
export async function patchEvent(
  token: string,
  id: number,
  body: PatchEventBody,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<CalendarEvent> {
  const payload = await requestJson(
    `/events/${id}`,
    { method: 'PATCH', token, body },
    fetchImpl,
    'patch event',
  );
  try {
    return parseCalendarEvent(payload);
  } catch (cause: unknown) {
    const detail = cause instanceof TypeError ? cause.message : 'malformed patch event response';
    throw new CalendarApiError(200, 'invalid_response', detail);
  }
}

/** Room kinds the control create endpoint accepts (ADR 0010, workshop merge / migration 0015). */
export type RoomKind = 'meeting' | 'webinar';

/**
 * Records the room (and its kind and lobby option) at the control service before the event's
 * first join (ADR 0010, CR-14): the kind is write-once - a webinar scheduled without this call
 * would come up as a meeting, and joining never asserts a kind; the lobby option rides the same
 * create. Best effort by design: a failure (network, 409 conflict when the slug is taken, 401 on
 * a dead session) never blocks the event creation - the room falls back to its stored state.
 * The bearer is REQUIRED now: CR-14 retired the unauthenticated ensure, and the legacy `{"name"}`
 * body answers 400 - this call speaks the new shape (`slug` + `kind` + `lobby`) through the
 * shared rooms client (web/src/api/rooms.ts).
 */
export async function ensureRoomKind(
  token: string,
  room: string,
  kind: RoomKind,
  options?: { readonly lobby?: boolean },
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<void> {
  try {
    await createRoom(
      token,
      { slug: room, kind, ...(options?.lobby === true ? { lobby: true } : {}) },
      fetchImpl,
    );
  } catch {
    // Best effort: the event is created regardless; the room keeps its stored kind.
  }
}

/** Deletes a local event (204); imported rows answer 409 read_only_source. */
export async function deleteEvent(
  token: string,
  id: number,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<void> {
  await request(`/events/${id}`, { method: 'DELETE', token }, fetchImpl, 'delete event');
}

/** Suggested download name when the export response carries no usable filename. */
const DEFAULT_ICS_FILENAME = 'calendar.ics';

/** filename="..." of a Content-Disposition attachment header (defensive: no match -> default). */
function filenameFromDisposition(header: string | null): string {
  if (header === null) {
    return DEFAULT_ICS_FILENAME;
  }
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  const name = match?.[1]?.trim();
  return name !== undefined && name.length > 0 ? name : DEFAULT_ICS_FILENAME;
}

/**
 * Downloads the whole calendar as one iCalendar body (GET /api/v1/events/export.ics): the
 * server caps at 500 events and keeps recurring rows verbatim. Returns the text plus the
 * filename from Content-Disposition so the caller can hand both to the browser download.
 */
export async function exportIcs(
  token: string,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<{ readonly filename: string; readonly body: string }> {
  const response = await request(
    '/events/export.ics',
    { method: 'GET', token },
    fetchImpl,
    'export ics',
  );
  let body = '';
  try {
    body = await response.text();
  } catch {
    throw new CalendarApiError(
      response.status,
      'invalid_response',
      'export ics body is unreadable',
    );
  }
  if (body.length === 0) {
    throw new CalendarApiError(response.status, 'invalid_response', 'export ics body is empty');
  }
  return { filename: filenameFromDisposition(response.headers.get('Content-Disposition')), body };
}

/** Narrows one import report; throws TypeError naming the first violating field. */
export function parseImportResult(value: unknown): ImportResult {
  if (!isRecord(value)) {
    throw new TypeError('import response must be a JSON object');
  }
  const imported = finiteNumber(value.imported);
  const updated = finiteNumber(value.updated);
  const failed = finiteNumber(value.failed);
  if (imported === null || updated === null || failed === null) {
    throw new TypeError('import response counts must be numbers');
  }
  const skipped: ImportSkipped[] = [];
  const skippedRaw = value.skipped === undefined || value.skipped === null ? [] : value.skipped;
  for (const row of parseArray(skippedRaw, 'skipped')) {
    if (!isRecord(row) || typeof row.reason !== 'string' || row.reason.length === 0) {
      throw new TypeError('import response skipped rows must carry a "reason" string');
    }
    skipped.push({ uid: typeof row.uid === 'string' ? row.uid : '', reason: row.reason });
  }
  const warnings: ImportWarning[] = [];
  const warningsRaw = value.warnings === undefined || value.warnings === null ? [] : value.warnings;
  for (const row of parseArray(warningsRaw, 'warnings')) {
    if (!isRecord(row) || typeof row.note !== 'string' || row.note.length === 0) {
      throw new TypeError('import response warning rows must carry a "note" string');
    }
    warnings.push({ uid: typeof row.uid === 'string' ? row.uid : '', note: row.note });
  }
  return { imported, updated, failed, skipped, warnings };
}

/**
 * Imports one raw iCalendar body (POST /api/v1/events/import.ics, text/calendar verbatim, the
 * server caps at 2 MiB / 500 VEVENTs). Upsert by UID; the answer reports imported/updated
 * counts plus per-VEVENT skipped rows and warnings - the caller surfaces them, never swallows.
 * Shares the JSON calls' 401-recovery (one refresh + one retry).
 */
export async function importIcs(
  token: string,
  icsBody: string,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<ImportResult> {
  const attempt = async (bearer: string): Promise<Response> => {
    try {
      return await fetchImpl(`${CALENDAR_API_BASE}/events/import.ics`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${bearer}`, 'Content-Type': 'text/calendar' },
        body: icsBody,
      });
    } catch {
      throw new CalendarApiError(0, 'network_error', 'calendar service is unreachable');
    }
  };
  let response = await attempt(token);
  if (response.status === 401) {
    const renewed = await recoverAccessToken(fetchImpl);
    if (renewed !== null) {
      response = await attempt(renewed);
    }
  }
  if (!response.ok) {
    throw await errorFromResponse(response, 'import ics');
  }
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new CalendarApiError(200, 'invalid_response', 'import ics is not valid JSON');
  }
  try {
    return parseImportResult(payload);
  } catch (cause: unknown) {
    const detail = cause instanceof TypeError ? cause.message : 'malformed import ics response';
    throw new CalendarApiError(200, 'invalid_response', detail);
  }
}

/** Lists the configured external calendar sources. */
export async function listSources(
  token: string,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<CalendarSource[]> {
  const body = await requestJson('/sources', { method: 'GET', token }, fetchImpl, 'list sources');
  if (!isRecord(body)) {
    throw new CalendarApiError(
      200,
      'invalid_response',
      'list sources response is not a JSON object',
    );
  }
  try {
    return parseArray(body.sources, 'sources').map(parseCalendarSource);
  } catch (cause: unknown) {
    throw parseError(200, 'list sources', cause);
  }
}

/** Creates a source; credentials travel write-only and are sealed server-side. */
export async function createSource(
  token: string,
  body: CreateSourceBody,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<CalendarSource> {
  const payload = await requestJson(
    '/sources',
    { method: 'POST', token, body },
    fetchImpl,
    'create source',
  );
  try {
    return parseCalendarSource(payload);
  } catch (cause: unknown) {
    const detail = cause instanceof TypeError ? cause.message : 'malformed create source response';
    throw new CalendarApiError(201, 'invalid_response', detail);
  }
}

/** Patches one source (name, config, credentials, enabled, interval); kind never changes. */
export async function patchSource(
  token: string,
  id: number,
  body: PatchSourceBody,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<CalendarSource> {
  const payload = await requestJson(
    `/sources/${id}`,
    { method: 'PATCH', token, body },
    fetchImpl,
    'patch source',
  );
  try {
    return parseCalendarSource(payload);
  } catch (cause: unknown) {
    const detail = cause instanceof TypeError ? cause.message : 'malformed patch source response';
    throw new CalendarApiError(200, 'invalid_response', detail);
  }
}

/** Deletes one source; already-imported events stay as read-only mirrors. */
export async function deleteSource(
  token: string,
  id: number,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<void> {
  await request(`/sources/${id}`, { method: 'DELETE', token }, fetchImpl, 'delete source');
}

/** Triggers an immediate sync; the answer is 202 and the outcome lands on the source row. */
export async function triggerSync(
  token: string,
  id: number,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<void> {
  await request(`/sources/${id}/sync`, { method: 'POST', token }, fetchImpl, 'trigger sync');
}

/**
 * Probes a not-yet-saved source (POST /api/v1/sources/check, CR-33): the config travels
 * verbatim for the kind, credentials stay optional and write-only. The endpoint answers HTTP
 * 200 both ways - a failed connection comes back as the {@link SourceCheck} answer
 * (ok=false + code + message); only contract violations (400 invalid_request) surface as
 * CalendarApiError.
 */
export async function checkSource(
  token: string,
  body: {
    readonly kind: 'caldav' | 'ews' | 'ad' | 'ics';
    readonly config: DistributiveOmit<SourceConfig, 'kind'>;
    readonly credentials?: SourceCredentials;
  },
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<SourceCheck> {
  const payload = await requestJson(
    '/sources/check',
    { method: 'POST', token, body },
    fetchImpl,
    'check source',
  );
  try {
    return parseSourceCheck(payload);
  } catch (cause: unknown) {
    const detail = cause instanceof TypeError ? cause.message : 'malformed check source response';
    throw new CalendarApiError(200, 'invalid_response', detail);
  }
}

/**
 * Re-probes a saved source with its stored credentials (POST /api/v1/sources/{id}/check,
 * CR-33); same answer shape as {@link checkSource}. 404 for an unknown id surfaces as
 * CalendarApiError.
 */
export async function checkSourceById(
  token: string,
  id: number,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<SourceCheck> {
  const payload = await requestJson(
    `/sources/${id}/check`,
    { method: 'POST', token },
    fetchImpl,
    'check saved source',
  );
  try {
    return parseSourceCheck(payload);
  } catch (cause: unknown) {
    const detail =
      cause instanceof TypeError ? cause.message : 'malformed check saved source response';
    throw new CalendarApiError(200, 'invalid_response', detail);
  }
}

/** Lists the AD room directory. */
export async function listAdRooms(
  token: string,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<AdRoom[]> {
  const body = await requestJson('/ad/rooms', { method: 'GET', token }, fetchImpl, 'list ad rooms');
  if (!isRecord(body)) {
    throw new CalendarApiError(
      200,
      'invalid_response',
      'list ad rooms response is not a JSON object',
    );
  }
  try {
    return parseArray(body.rooms, 'rooms').map(parseAdRoom);
  } catch (cause: unknown) {
    throw parseError(200, 'list ad rooms', cause);
  }
}
